<template>
  <v-card id="acceptance-pra" class="pa-4 mb-4">
    <div class="d-flex flex-wrap align-center mb-3">
      <span class="text-subtitle-1 font-weight-medium mr-2">
        {{ $t("acceptance.pra.title") }}
      </span>
      <v-chip
        v-if="chip"
        size="small"
        variant="tonal"
        :color="chip.color"
        class="mr-2"
      >
        {{ $t(chip.key) }}
      </v-chip>
      <v-chip v-if="flags.onHold" size="small" variant="tonal" color="purple">
        {{ $t("acceptance.pra.on_hold") }}
      </v-chip>
      <span v-if="dueDateText" class="text-body-2 ml-auto">
        {{ $t("acceptance.deadlines.deregistration") }}
        <strong>{{ dueDateText }}</strong>
      </span>
    </div>

    <v-skeleton-loader v-if="state === 'loading'" type="text, text" />

    <div v-else-if="state === 'draft'" class="text-body-2 text-grey-darken-1">
      {{ $t("acceptance.pra.opens_at_confirm") }}
    </div>

    <div v-else-if="state === 'no_case'" class="text-body-2 text-grey-darken-1">
      {{ $t("acceptance.pra.not_opened") }}
    </div>

    <v-alert
      v-else-if="state === 'error'"
      type="warning"
      variant="tonal"
      density="compact"
    >
      <div class="d-flex flex-wrap align-center">
        <span class="mr-auto py-1">{{ $t("acceptance.pra.unavailable") }}</span>
        <v-btn
          size="small"
          variant="text"
          color="primary"
          @click="$emit('reload')"
        >
          {{ $t("acceptance.conflict.reload") }}
        </v-btn>
      </div>
    </v-alert>

    <template v-else-if="view">
      <!-- Someone else changed the case (409): only the case reloads. -->
      <v-alert
        v-if="conflict"
        type="info"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        <div class="d-flex flex-wrap align-center">
          <div class="mr-auto py-1">
            <strong>{{ $t("acceptance.conflict.title") }}</strong>
          </div>
          <v-btn color="primary" variant="flat" @click="$emit('reload')">
            {{ $t("acceptance.conflict.reload") }}
          </v-btn>
        </div>
      </v-alert>

      <!-- On hold: no step can be recorded until the hold is released (any User, Manager or
           Admin). The hold file is optional, so it is replaced or removed like the request's. -->
      <v-alert
        v-if="activeHold"
        color="purple"
        variant="tonal"
        density="compact"
        icon="mdi-pause-circle-outline"
        class="mb-3"
      >
        <div class="d-flex flex-wrap align-start ga-2">
          <div class="mr-auto">
            <div class="font-weight-medium">
              {{
                $t("acceptance.pra.hold.banner_title", {
                  type: holdTypeText(activeHold.type),
                })
              }}
            </div>
            <div class="text-body-2 pra-note">{{ activeHold.note }}</div>
            <div class="text-caption">
              {{ placedLine(activeHold, "DD/MM/YYYY HH:mm") }}
              <template v-if="holdFile">
                ·
                <a
                  href="#"
                  class="text-purple"
                  @click.prevent="$emit('open-file', holdFile.id)"
                >
                  {{ holdFile.originalName || $t("acceptance.pra.file") }}
                </a>
                <template v-if="holdFileAccess">
                  <v-btn
                    size="x-small"
                    variant="text"
                    color="primary"
                    class="ml-1"
                    :disabled="busy || conflict"
                    @click="$emit('replace-file', holdFile, holdFileAccess)"
                  >
                    {{ $t("acceptance.pra.file_change.action") }}
                  </v-btn>
                  <v-btn
                    size="x-small"
                    variant="text"
                    color="error"
                    :disabled="busy || conflict"
                    @click="$emit('remove-file', holdFile, holdFileAccess)"
                  >
                    {{ $t("acceptance.pra.file_change.remove_action") }}
                  </v-btn>
                </template>
              </template>
            </div>
          </div>
          <v-btn
            variant="outlined"
            color="primary"
            :disabled="busy || conflict"
            @click="$emit('release-hold')"
          >
            {{ $t("acceptance.pra.hold.release_action") }}
          </v-btn>
        </div>
      </v-alert>

      <v-alert
        v-if="flags.late"
        type="error"
        variant="tonal"
        density="compact"
        icon="mdi-clock-alert-outline"
        class="mb-3"
      >
        <strong>{{ $t("acceptance.pra.late_title") }}</strong>
        {{ $t("acceptance.pra.late_body", { date: dueDateText }) }}
      </v-alert>

      <!-- Closed through the receipt (Radiata): only corrections remain (slice 9). -->
      <v-alert
        v-if="confirmed"
        type="success"
        variant="tonal"
        density="compact"
        icon="mdi-check-circle-outline"
        class="mb-3"
      >
        {{
          $t("acceptance.pra.receipt.confirmed_banner", {
            date: civilDate(confirmed.date),
            number: confirmed.number || "—",
          })
        }}
      </v-alert>

      <!-- Wraps on a phone, where the connecting lines are hidden. -->
      <div class="d-flex flex-wrap flex-sm-nowrap align-center ga-3 mb-3">
        <template v-for="(step, index) in steps" :key="step.key">
          <div class="d-flex align-center flex-shrink-0">
            <v-avatar
              size="24"
              class="text-caption mr-2"
              :color="stepColor(step)"
              :variant="step.status === 'done' ? 'flat' : 'outlined'"
            >
              {{ index + 1 }}
            </v-avatar>
            <span class="text-body-2">
              {{ $t(`acceptance.pra.steps.${step.key}`) }}
            </span>
          </div>
          <v-divider
            v-if="index < steps.length - 1"
            class="d-none d-sm-block pra-step-line"
          />
        </template>
      </div>

      <div class="text-body-2 text-grey-darken-1 mb-3">
        {{ $t("acceptance.pra.car_pra", { number: view.carPra || "—" }) }}
        ·
        {{ $t(openingKey, { date: openedOn }) }}
      </div>

      <v-alert
        v-for="warning in alerts"
        :key="warning.code"
        type="warning"
        variant="tonal"
        density="compact"
        class="mb-3"
      >
        {{ $t(`acceptance.pra.warnings.${warning.code}`) }}
      </v-alert>

      <!-- Recorded steps, one section each. Files change only here (decision Q3, revised): a
           required document is replaced, an optional one is also removed. A Manager / Admin
           corrects each recorded value ("Correggi", slice 9), also on hold and once confirmed. -->
      <div v-if="praCase && praCase.crd" class="pra-step-section pt-3 mb-3">
        <div class="text-body-2 font-weight-medium mb-1">
          {{ $t("acceptance.pra.crd.title") }}
        </div>
        <div class="text-body-2">
          {{ $t("acceptance.pra.crd.number") }}:
          <strong>{{ praCase.crd.number }}</strong>
          <v-btn
            v-if="canCorrect"
            size="x-small"
            variant="text"
            color="primary"
            :disabled="busy || conflict"
            @click="$emit('correct', 'crd.number')"
          >
            {{ $t("acceptance.corrections.correct") }}
          </v-btn>
          · {{ $t("acceptance.pra.crd.issued_on") }}:
          <strong>{{ civilDate(praCase.crd.issuedOn) }}</strong>
          <v-btn
            v-if="canCorrect"
            size="x-small"
            variant="text"
            color="primary"
            :disabled="busy || conflict"
            @click="$emit('correct', 'crd.issuedOn')"
          >
            {{ $t("acceptance.corrections.correct") }}
          </v-btn>
        </div>
        <correction-note
          v-for="group in ['crd.number', 'crd.issuedOn']"
          :key="group"
          :group="group"
          frozen
          part="history"
          :fieldset="fieldset"
          :corrections="corrections"
          @open-log="$emit('open-log', $event)"
        />
        <div v-if="crdFile" class="text-body-2">
          {{ $t("acceptance.pra.file") }}:
          <a
            href="#"
            class="text-primary"
            @click.prevent="$emit('open-file', crdFile.id)"
          >
            {{ crdFile.originalName || $t("acceptance.pra.crd.title") }}
          </a>
          <v-btn
            v-if="crdFileAccess"
            size="x-small"
            variant="text"
            color="primary"
            class="ml-1"
            :disabled="busy || conflict"
            @click="$emit('replace-file', crdFile, crdFileAccess)"
          >
            {{ $t("acceptance.pra.file_change.action") }}
          </v-btn>
        </div>
        <div class="text-caption text-grey-darken-1">
          {{ recordedLine(praCase.crd) }}
        </div>
      </div>

      <div v-if="praCase && praCase.request" class="pra-step-section pt-3 mb-3">
        <div class="d-flex flex-wrap align-center ga-2 mb-1">
          <span class="text-body-2 font-weight-medium">
            {{ $t("acceptance.pra.request.title") }}
          </span>
          <v-chip
            v-if="praCase.request.lateAtRecording"
            size="small"
            variant="tonal"
            color="amber-darken-2"
          >
            {{
              $t("acceptance.pra.request.late_chip", {
                date: civilDate(praCase.request.dueDateAtRecording),
              })
            }}
          </v-chip>
        </div>
        <div class="text-body-2">
          {{ $t("acceptance.pra.request.date") }}:
          <strong>{{ civilDate(praCase.request.date) }}</strong>
          <v-btn
            v-if="canCorrect"
            size="x-small"
            variant="text"
            color="primary"
            :disabled="busy || conflict"
            @click="$emit('correct', 'request.date')"
          >
            {{ $t("acceptance.corrections.correct") }}
          </v-btn>
          · {{ $t("acceptance.pra.request.channel") }}:
          <strong>{{ channelText(praCase.request) }}</strong>
          <v-btn
            v-if="canCorrect"
            size="x-small"
            variant="text"
            color="primary"
            :disabled="busy || conflict"
            @click="$emit('correct', 'request.channel')"
          >
            {{ $t("acceptance.corrections.correct") }}
          </v-btn>
        </div>
        <correction-note
          v-for="group in ['request.date', 'request.channel']"
          :key="group"
          :group="group"
          frozen
          part="history"
          :fieldset="fieldset"
          :corrections="corrections"
          @open-log="$emit('open-log', $event)"
        />
        <div v-if="requestFile" class="text-body-2">
          {{ $t("acceptance.pra.file") }}:
          <a
            href="#"
            class="text-primary"
            @click.prevent="$emit('open-file', requestFile.id)"
          >
            {{ requestFile.originalName || $t("acceptance.pra.request.title") }}
          </a>
          <template v-if="requestFileAccess">
            <v-btn
              size="x-small"
              variant="text"
              color="primary"
              class="ml-1"
              :disabled="busy || conflict"
              @click="$emit('replace-file', requestFile, requestFileAccess)"
            >
              {{ $t("acceptance.pra.file_change.action") }}
            </v-btn>
            <v-btn
              size="x-small"
              variant="text"
              color="error"
              :disabled="busy || conflict"
              @click="$emit('remove-file', requestFile, requestFileAccess)"
            >
              {{ $t("acceptance.pra.file_change.remove_action") }}
            </v-btn>
          </template>
        </div>
        <div class="text-caption text-grey-darken-1">
          {{ recordedLine(praCase.request) }}
        </div>
      </div>

      <div v-if="praCase && praCase.receipt" class="pra-step-section pt-3 mb-3">
        <div class="text-body-2 font-weight-medium mb-1">
          {{ $t("acceptance.pra.receipt.title") }}
        </div>
        <div class="text-body-2">
          {{ $t("acceptance.pra.receipt.number") }}:
          <strong>{{ praCase.receipt.number }}</strong>
          <v-btn
            v-if="canCorrect"
            size="x-small"
            variant="text"
            color="primary"
            :disabled="busy || conflict"
            @click="$emit('correct', 'receipt.number')"
          >
            {{ $t("acceptance.corrections.correct") }}
          </v-btn>
          · {{ $t("acceptance.pra.receipt.date") }}:
          <strong>{{ civilDate(praCase.receipt.date) }}</strong>
          <v-btn
            v-if="canCorrect"
            size="x-small"
            variant="text"
            color="primary"
            :disabled="busy || conflict"
            @click="$emit('correct', 'receipt.date')"
          >
            {{ $t("acceptance.corrections.correct") }}
          </v-btn>
        </div>
        <correction-note
          v-for="group in ['receipt.number', 'receipt.date']"
          :key="group"
          :group="group"
          frozen
          part="history"
          :fieldset="fieldset"
          :corrections="corrections"
          @open-log="$emit('open-log', $event)"
        />
        <div v-if="receiptFile" class="text-body-2">
          {{ $t("acceptance.pra.file") }}:
          <a
            href="#"
            class="text-primary"
            @click.prevent="$emit('open-file', receiptFile.id)"
          >
            {{ receiptFile.originalName || $t("acceptance.pra.receipt.title") }}
          </a>
          <v-btn
            v-if="receiptFileAccess"
            size="x-small"
            variant="text"
            color="primary"
            class="ml-1"
            :disabled="busy || conflict"
            @click="$emit('replace-file', receiptFile, receiptFileAccess)"
          >
            {{ $t("acceptance.pra.file_change.action") }}
          </v-btn>
        </div>
        <div class="text-caption text-grey-darken-1">
          {{ recordedLine(praCase.receipt) }}
        </div>
      </div>

      <!-- The next step and "Metti in sospeso". On hold the step stays visible but disabled; a
           confirmed case has neither. -->
      <div
        v-if="crdAction || requestAction || receiptAction || holdAction"
        class="d-flex flex-wrap align-center ga-2"
      >
        <v-btn
          v-if="crdAction"
          color="primary"
          variant="flat"
          :disabled="crdAction.disabled || busy || conflict"
          @click="$emit('record-crd')"
        >
          {{ $t("acceptance.pra.crd.action") }}
        </v-btn>
        <v-btn
          v-if="requestAction"
          color="primary"
          variant="flat"
          :disabled="requestAction.disabled || busy || conflict"
          @click="$emit('record-request')"
        >
          {{ $t("acceptance.pra.request.action") }}
        </v-btn>
        <v-btn
          v-if="receiptAction"
          color="primary"
          variant="flat"
          :disabled="receiptAction.disabled || busy || conflict"
          @click="$emit('record-receipt')"
        >
          {{ $t("acceptance.pra.receipt.action") }}
        </v-btn>
        <v-btn
          v-if="holdAction"
          color="primary"
          variant="outlined"
          :disabled="busy || conflict"
          @click="$emit('place-hold')"
        >
          {{ $t("acceptance.pra.hold.action") }}
        </v-btn>
        <span v-if="activeHold" class="text-body-2 text-grey-darken-1">
          {{ $t("acceptance.pra.hold.blocked") }}
        </span>
      </div>

      <!-- Released holds, newest first: shown even when there are none. -->
      <div class="mt-3">
        <a
          href="#"
          class="text-body-2 text-grey-darken-3 text-decoration-none"
          @click.prevent="showPastHolds = !showPastHolds"
        >
          <v-icon
            size="small"
            :icon="showPastHolds ? 'mdi-chevron-down' : 'mdi-chevron-right'"
          />
          {{ $t("acceptance.pra.hold.previous", { count: pastHolds.length }) }}
        </a>
        <v-expand-transition>
          <div v-if="showPastHolds" class="pt-2">
            <div
              v-if="!pastHolds.length"
              class="text-body-2 text-grey-darken-1"
            >
              {{ $t("acceptance.pra.hold.previous_empty") }}
            </div>
            <div
              v-for="hold in pastHolds"
              :key="hold.holdId"
              class="pra-step-section py-2"
            >
              <div class="text-body-2 font-weight-medium">
                {{ holdTypeText(hold.type) }}
              </div>
              <div class="text-body-2 pra-note">{{ hold.note }}</div>
              <div class="text-caption text-grey-darken-1">
                {{ placedLine(hold, "DD/MM/YYYY") }} ·
                {{ releasedLine(hold) }}
              </div>
              <div class="text-caption text-grey-darken-1 pra-note">
                {{
                  $t("acceptance.history.reason", {
                    reason: hold.releaseReason,
                  })
                }}
              </div>
              <div v-if="fileById(hold.fileId)" class="text-caption">
                {{ $t("acceptance.pra.file") }}:
                <a
                  href="#"
                  class="text-primary"
                  @click.prevent="$emit('open-file', hold.fileId)"
                >
                  {{
                    fileById(hold.fileId).originalName ||
                    $t("acceptance.pra.file_steps.hold")
                  }}
                </a>
              </div>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </template>
  </v-card>
</template>

<script>
import moment from "moment";
import { formatCivilDate } from "@/components/Acceptance/deadlines.mjs";
import CorrectionNote from "@/components/Acceptance/correctionNote.vue";
import { PRA_FIELDSET } from "@/components/Acceptance/praCorrections.mjs";
import {
  bannerFlags,
  openingKey,
  stateChip,
  stepperSteps,
} from "@/components/Acceptance/praView.mjs";
import { crdAction } from "@/components/Acceptance/praCrdForm.mjs";
import { requestAction } from "@/components/Acceptance/praRequestForm.mjs";
import {
  confirmedBanner,
  receiptAction,
} from "@/components/Acceptance/praReceiptForm.mjs";
import { praFileAccess } from "@/components/Acceptance/praFiles.mjs";
import {
  holdAction,
  holdTypeKey,
  pastHolds,
} from "@/components/Acceptance/praHoldForm.mjs";

// The PRA deregistration case of a confirmed acceptance (Stage 2 plan §D): its state, the recorded
// steps and the next step's button (the CRD from slice 4, the request from slice 6, the receipt
// from slice 7). A confirmed case (Radiata) shows the green banner and no step or hold button.
// Holds (slice 8): the purple banner with "Rilascia sospensione", the step button disabled, and
// the released holds. Corrections (slice 9): "Correggi" next to each recorded value for a Manager /
// Admin (view.canCorrect, cosmetic: the API decides) and "Valore precedente" from view.corrections.
// The page sends every write through the case session.
export default {
  name: "AcceptancePraCase",
  components: { "correction-note": CorrectionNote },
  props: {
    // "draft" | "loading" | "no_case" | "error" | "case" (praView.cardState)
    state: { type: String, required: true },
    // GET /compliance/dossiers/:id/pra-case, or null
    view: { type: Object, default: null },
    // The case session's 409 (a second revision, separate from the dossier's)
    conflict: { type: Boolean, default: false },
    // A PRA write is running on the case session
    busy: { type: Boolean, default: false },
    // { role, userId } of the logged-in user, for the cosmetic "Sostituisci" / "Rimuovi" rule
    user: { type: Object, default: () => ({}) },
  },
  emits: [
    "reload",
    "record-crd",
    "record-request",
    "record-receipt",
    "place-hold",
    "release-hold",
    "open-file",
    "replace-file",
    "remove-file",
    "correct",
    "open-log",
  ],
  data: () => ({
    showPastHolds: false,
  }),
  computed: {
    praCase() {
      return this.view && this.state === "case" ? this.view.praCase : null;
    },
    chip() {
      return stateChip(this.praCase);
    },
    flags() {
      return bannerFlags(this.praCase ? this.view : null);
    },
    steps() {
      return stepperSteps(this.praCase && this.praCase.state);
    },
    dueDateText() {
      return this.praCase ? formatCivilDate(this.view.dueDate) : "";
    },
    openingKey() {
      return openingKey(this.praCase);
    },
    openedOn() {
      return this.praCase && this.praCase.openedAt
        ? moment(this.praCase.openedAt).format("DD/MM/YYYY")
        : "—";
    },
    // The API's warnings, without request_late: the request section's chip already shows it.
    alerts() {
      return ((this.view && this.view.warnings) || []).filter(
        (warning) => warning.code !== "request_late",
      );
    },
    crdAction() {
      return this.praCase ? crdAction(this.view) : null;
    },
    requestAction() {
      return this.praCase ? requestAction(this.view) : null;
    },
    receiptAction() {
      return this.praCase ? receiptAction(this.view) : null;
    },
    // { date, number } of the receipt once the case is confirmed, else null.
    confirmed() {
      return confirmedBanner(this.praCase);
    },
    crdFile() {
      return this.stepFile("crd");
    },
    // null, or { reasonRequired } when this user may replace the CRD file (the API decides).
    crdFileAccess() {
      return this.fileAccess(this.crdFile);
    },
    requestFile() {
      return this.stepFile("request");
    },
    // The request file is optional: the same access allows "Sostituisci" and "Rimuovi".
    requestFileAccess() {
      return this.fileAccess(this.requestFile);
    },
    receiptFile() {
      return this.stepFile("receipt");
    },
    // The receipt is required: replaced, never removed (Q3). Once confirmed, only a Manager / Admin
    // with a reason (praFileAccess), as the API.
    receiptFileAccess() {
      return this.fileAccess(this.receiptFile);
    },
    activeHold() {
      return (this.praCase && this.praCase.activeHold) || null;
    },
    holdAction() {
      return this.praCase ? holdAction(this.view) : null;
    },
    holdFile() {
      return this.activeHold ? this.fileById(this.activeHold.fileId) : null;
    },
    // The hold file is optional: the same access allows "Sostituisci" and "Rimuovi".
    holdFileAccess() {
      return this.fileAccess(this.holdFile);
    },
    pastHolds() {
      return pastHolds(this.praCase);
    },
    canCorrect() {
      return Boolean(this.praCase && this.view.canCorrect === true);
    },
    corrections() {
      return (this.view && this.view.corrections) || {};
    },
    fieldset() {
      return PRA_FIELDSET;
    },
  },
  methods: {
    // The file of a recorded step, from the case's files (a bare { id } if it is not listed).
    stepFile(step) {
      const recorded = this.praCase && this.praCase[step];
      if (!recorded || !recorded.fileId) return null;
      return (
        (this.view.files || []).find((file) => file.id === recorded.fileId) || {
          id: recorded.fileId,
        }
      );
    },
    // A file of the case by id (a bare { id } if it is not listed), or null without an id.
    fileById(id) {
      if (!id) return null;
      return (this.view.files || []).find((file) => file.id === id) || { id };
    },
    fileAccess(file) {
      if (!file || !file.uploadedBy) return null;
      return praFileAccess(file, {
        ...this.user,
        caseState: this.praCase.state,
      });
    },
    // "Agenzia STA · Studio Auto Rossi", or the channel alone.
    channelText(request) {
      const channel = this.$t(
        `acceptance.pra.request.channels.${request.channel}`,
      );
      return request.agencyName
        ? `${channel} · ${request.agencyName}`
        : channel;
    },
    civilDate(value) {
      return formatCivilDate(value) || "—";
    },
    holdTypeText(type) {
      const key = holdTypeKey(type);
      return key ? this.$t(key) : type || "—";
    },
    // "Sospesa da Anna Neri il 19/10/2026 09:30"
    placedLine(hold, format) {
      return this.$t("acceptance.pra.hold.placed_line", {
        name: (hold.placedBy && hold.placedBy.name) || "—",
        date: hold.placedAt ? moment(hold.placedAt).format(format) : "—",
      });
    },
    // "rilasciata da Marco Sala il 22/10/2026"
    releasedLine(hold) {
      return this.$t("acceptance.pra.hold.released_line", {
        name: (hold.releasedBy && hold.releasedBy.name) || "—",
        date: hold.releasedAt
          ? moment(hold.releasedAt).format("DD/MM/YYYY")
          : "—",
      });
    },
    // "Origine: Manuale · registrato da Sara Galli il 21/10/2026"
    recordedLine(step) {
      const by = (step.verifiedBy && step.verifiedBy.name) || "—";
      const date = step.recordedAt
        ? moment(step.recordedAt).format("DD/MM/YYYY")
        : "—";
      return this.$t("acceptance.pra.recorded_line", {
        source: this.$t(`acceptance.pra.sources.${step.source || "manual"}`),
        name: by,
        date,
      });
    },
    stepColor(step) {
      if (step.status === "todo") return "grey";
      return this.flags.confirmed ? "green" : "primary";
    },
  },
};
</script>

<style scoped>
/* Vuetify gives a divider flex-basis 100%; the lines share the space left by the steps. */
.pra-step-line {
  flex: 1 1 0;
  min-width: 12px;
}
.pra-step-section {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}
.pra-note {
  white-space: pre-line;
  overflow-wrap: anywhere;
}
</style>
